<script lang="ts" setup>
import {
    faCloudArrowUp,
    faCloudUpload,
    faImage,
    faSearch,
    faShare,
    faSpinnerThird,
    faTimes,
} from "@fortawesome/pro-light-svg-icons";
import { faUnsplash } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import MediaUpload from "~/components/MediaUpload.vue";
import type { Media } from "~/stores/media-library";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { useClipboard } from "#imports";
import { faHome } from "@fortawesome/pro-regular-svg-icons";

const library = useMediaLibrary();
const highlight = ref<Media>();
const block_update = ref<boolean>(false);

const show = defineModel<boolean>({ default: false });

const props = withDefaults(
    defineProps<{
        startAt?: string;
        customLabel?: string;
        custom?: Media[];
    }>(),
    {
        custom: () => [],
    },
);

const dropZoneRef = ref();
const opener = ref();
const { isOverDropZone } = useDropZone(dropZoneRef, {
    onDrop: (files) =>
        library.handleFileUploads((files ?? []) as unknown as FileList),
});

const { isOverDropZone: isOverOpenerDropZone } = useDropZone(opener, {
    onDrop: (files) => {
        show.value = true;
        library.handleFileUploads((files ?? []) as unknown as FileList);
    },
});

function share(asset: Media) {
    const { copy } = useClipboard({
        source: asset.full_url,
        legacy: true,
    });

    copy(asset.full_url);

    useToast().add({
        title: "URL Copied",
        actions: [
            {
                label: "Open URL",
                click: () => window.open(asset.full_url, "_blank"),
            },
        ],
    });
}

onBeforeMount(() => {
    if (library.assets.length === 0) {
        library.refresh();
    }
});

watch(
    [() => highlight.value?.name, () => highlight.value?.metadata],
    useDebounce(() => {
        if (block_update.value) {
            block_update.value = false;
            return;
        }
        if (highlight.value) {
            library.updateMedia(highlight.value);
        }
    }),
    { deep: true },
);

const { user } = useUser();

onBeforeMount(() => {
    if (props.startAt) {
        library.current = props.startAt;
    }
});
</script>

<template>
    <div v-if="$slots.default" ref="opener">
        <slot
            :isOverDropZone="isOverOpenerDropZone"
            :show="show"
            :toggle="() => (show = !show)"
        />
    </div>
    <UModal
        v-model="show"
        :ui="{
            width: 'w-full sm:max-w-3xl lg:max-w-7xl 2xl:max-w-[75vw]',
            height: 'max-h-[calc(100vh-4rem)] lg:max-h-[80vh]',
        }"
    >
        <UCard
            :ui="{
                body: {
                    base: 'flex-1 overscroll-contain gap-6 overflow-auto flex min-h-content',
                    padding: '',
                },
            }"
            class="max-h-full flex flex-col overflow-auto"
        >
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="font-display text-lg flex gap-4 items-center">
                        <FontAwesomeIcon :icon="faImage" />
                        <span>Media Library</span>
                    </h3>
                    <div class="flex-1 flex px-8">
                        <UButtonGroup
                            size="xs"
                            class="grid"
                            :class="
                                user?.flags?.is_unsplash_enabled &&
                                custom.length
                                    ? 'grid-cols-3'
                                    : 'grid-cols-2'
                            "
                            :ui="{}"
                            v-if="
                                user?.flags?.is_unsplash_enabled ||
                                custom.length
                            "
                        >
                            <UButton
                                :color="
                                    library.current === 'home'
                                        ? 'gray'
                                        : 'white'
                                "
                                variant="soft"
                                label="Home"
                                class="justify-center active:scale-100"
                                @click="library.current = 'home'"
                            >
                                <template #leading>
                                    <FontAwesomeIcon
                                        :icon="faHome"
                                        fixed-width
                                    />
                                </template>
                            </UButton>
                            <UButton
                                v-if="user?.flags?.is_unsplash_enabled"
                                :color="
                                    library.current === 'unsplash'
                                        ? 'gray'
                                        : 'white'
                                "
                                variant="soft"
                                label="Unsplash"
                                class="justify-center active:scale-100"
                                @click="
                                    library.current = 'unsplash';
                                    library.query.search = library.query.search
                                        ? library.query.search
                                        : 'creative';
                                "
                            >
                                <template #leading>
                                    <FontAwesomeIcon
                                        :icon="faUnsplash"
                                        fixed-width
                                    />
                                </template>
                            </UButton>
                            <UButton
                                v-if="custom.length"
                                :color="
                                    library.current === 'custom'
                                        ? 'gray'
                                        : 'white'
                                "
                                variant="soft"
                                label="Unsplash"
                                class="justify-center active:scale-100"
                                @click="library.current = 'custom'"
                            >
                                <template #leading v-if="$slots['custom-icon']">
                                    <slot name="custom-icon" />
                                </template>
                                {{ customLabel }}
                            </UButton>
                        </UButtonGroup>
                    </div>
                    <UButton
                        color="gray"
                        size="sm"
                        square
                        variant="ghost"
                        @click="show = false"
                    >
                        <FontAwesomeIcon :icon="faTimes" size="lg" />
                    </UButton>
                </div>
            </template>
            <template #default>
                <div
                    ref="dropZoneRef"
                    :class="{ 'pr-4 sm:pr-6': !highlight }"
                    class="flex relative flex-col overflow-auto flex-1 gap-4 pl-4 py-5 sm:pl-6 transition duration-150"
                >
                    <div
                        :class="{
                            'opacity-0 pointer-events-none': !isOverDropZone,
                            'opacity-100': isOverDropZone,
                            'pr-4 sm:pr-6': !highlight,
                        }"
                        class="absolute z-20 bg-white top-0 left-0 rounded-lg w-full h-full pl-4 py-5 sm:pl-6"
                    >
                        <Placeholder
                            class="w-full h-full flex-col rounded-lg flex items-center justify-center"
                        >
                            <div class="z-20 flex flex-col gap-6 items-center">
                                <FontAwesomeLayers
                                    class="flex items-center justify-center fa-3x"
                                    fixed-width
                                >
                                    <FontAwesomeIcon
                                        :icon="faCircle"
                                        class="text-primary-100"
                                    />
                                    <FontAwesomeIcon
                                        :icon="faCloudUpload"
                                        class="text-primary-500"
                                        transform="shrink-7"
                                    />
                                </FontAwesomeLayers>
                                <h4 class="font-display font-medium text-2xl">
                                    Drop files here
                                </h4>
                                <p class="-mt-2 text-hd-neutral-500">
                                    Drop your files to instantly upload them to
                                    your media library
                                </p>
                            </div>
                        </Placeholder>
                    </div>
                    <MediaUpload
                        v-for="upload in library.uploads"
                        :key="upload.id"
                        :upload="upload"
                    />
                    <div class="flex items-center gap-4 justify-between">
                        <UInput
                            v-model="library.query.search"
                            :ui="{ icon: { trailing: { pointer: '' } } }"
                            class="flex-1"
                            placeholder="Search"
                            size="lg"
                        >
                            <template #leading>
                                <FontAwesomeIcon
                                    v-if="library.searching"
                                    :icon="faSpinnerThird"
                                    class="opacity-25"
                                    spin
                                />
                                <FontAwesomeIcon
                                    v-else
                                    :icon="faSearch"
                                    class="opacity-25"
                                />
                            </template>
                        </UInput>
                        <UButton
                            color="white"
                            size="lg"
                            @click="library.openFileDialog"
                        >
                            <template #leading>
                                <FontAwesomeIcon :icon="faCloudArrowUp" />
                            </template>
                            Upload media
                        </UButton>
                    </div>
                    <div v-if="library.info.total">
                        <div class="font-medium">
                            {{ library.info.total }} items
                        </div>
                    </div>
                    <div
                        v-if="
                            library.current === 'custom'
                                ? custom
                                : library.assets.length
                        "
                        class="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 auto-rows-min gap-4 flex-1 overscroll-contain overflow-y-auto min-h-content"
                    >
                        <Placeholder
                            v-for="asset in library.current === 'custom'
                                ? custom
                                : library.assets"
                            :key="asset.id"
                            class="p-2 rounded-lg h-auto"
                            @click="
                                block_update = true;
                                highlight = asset;
                            "
                        >
                            <nuxt-img
                                :src="
                                    asset.preview_url
                                        ? asset.preview_url
                                        : asset.full_url
                                "
                                class="relative z-10 mx-auto rounded-lg"
                            />
                        </Placeholder>
                    </div>
                    <div v-else>
                        <Placeholder
                            class="w-full h-full py-24 flex-col rounded-lg flex items-center justify-center"
                        >
                            <div class="z-20 flex flex-col gap-6 items-center">
                                <FontAwesomeLayers
                                    class="flex items-center justify-center fa-3x"
                                    fixed-width
                                >
                                    <FontAwesomeIcon
                                        :icon="faCircle"
                                        class="text-primary-100"
                                    />
                                    <FontAwesomeIcon
                                        :icon="faCloudUpload"
                                        class="text-primary-500"
                                        transform="shrink-7"
                                    />
                                </FontAwesomeLayers>
                                <h4 class="font-display font-medium text-2xl">
                                    Drop files here
                                </h4>
                                <p class="-mt-2 text-hd-neutral-500">
                                    Drop your files to instantly upload them to
                                    your media library
                                </p>
                            </div>
                        </Placeholder>
                    </div>
                    <div v-if="library.info.total" class="flex justify-between">
                        <AppListPaginationFooter
                            v-model:page="library.query.page"
                            v-model:show="library.query.per_page"
                            :options="[5, 10, 15, 20, 25, 50, 100]"
                            :total="library.info.total"
                            label="items"
                        />
                    </div>
                </div>
                <div
                    v-if="highlight"
                    class="relative max-h-full pr-4 py-5 sm:pr-6"
                >
                    <UCard
                        :ui="{
                            body: { base: 'overflow-auto flex-1', padding: '' },
                        }"
                        class="w-[28rem] max-h-full h-full flex flex-col"
                    >
                        <template #header>
                            <h3
                                class="font-display w-full text-lg flex gap-4 items-center"
                            >
                                <FontAwesomeIcon :icon="faImage" />
                                <span class="truncate flex-1">{{
                                    highlight.name
                                }}</span>
                                <UButton
                                    class="w-8 h-8 justify-center"
                                    color="white"
                                    square
                                    @click="share(highlight)"
                                >
                                    <FontAwesomeIcon
                                        :icon="faShare"
                                        class="text-lg"
                                    />
                                </UButton>
                                <UButton
                                    class="w-8 h-8 -ml-2 justify-center"
                                    color="white"
                                    square
                                    @click="highlight = undefined"
                                >
                                    <FontAwesomeIcon
                                        :icon="faTimes"
                                        class="text-lg"
                                    />
                                </UButton>
                            </h3>
                        </template>
                        <div
                            class="flex overflow-y-auto flex-1 flex-col gap-6 px-4 sm:px-6 py-5"
                        >
                            <Placeholder class="p-2 rounded-lg">
                                <nuxt-img
                                    :key="highlight.id"
                                    :src="highlight.full_url"
                                    class="relative w-auto z-10 max-h-72 mx-auto rounded-lg"
                                />
                            </Placeholder>
                            <UTabs
                                :items="[
                                    {
                                        label: 'Description',
                                        slot: 'description',
                                    },
                                    { label: 'More info', slot: 'info' },
                                ]"
                            >
                                <template #description>
                                    <div class="flex flex-col gap-4 pt-2">
                                        <UFormGroup label="Title">
                                            <UInput
                                                v-model="highlight.name"
                                                class="flex-1"
                                                :disabled="
                                                    library.current === 'custom'
                                                "
                                                placeholder="Name"
                                                size="lg"
                                            />
                                        </UFormGroup>
                                        <UFormGroup
                                            help="Falls back to image title if left empty."
                                            label="Alt text"
                                        >
                                            <UInput
                                                v-model="
                                                    highlight.metadata.alt_text
                                                "
                                                :disabled="
                                                    library.current === 'custom'
                                                "
                                                class="flex-1"
                                                placeholder=""
                                                size="lg"
                                            />
                                        </UFormGroup>
                                        <UProgress
                                            v-if="library.updating"
                                            animation="carousel"
                                            size="sm"
                                        />
                                    </div>
                                </template>
                                <template #info>
                                    <UIDataList
                                        class="bg-hd-neutral-50 rounded-lg mt-4"
                                        condensed
                                    >
                                        <UIDataListItem condensed>
                                            <template #label>Id</template>
                                            <template #value
                                                ><span class="truncate">{{
                                                    highlight.id
                                                }}</span>
                                            </template>
                                        </UIDataListItem>
                                        <UIDataListItem condensed>
                                            <template #label
                                                >Mime type
                                            </template>
                                            <template #value
                                                ><span class="truncate">{{
                                                    highlight.mime_type
                                                }}</span>
                                            </template>
                                        </UIDataListItem>
                                    </UIDataList>
                                </template>
                            </UTabs>
                        </div>
                        <template v-if="$slots.action" #footer>
                            <slot
                                :selected="highlight"
                                :toggle="() => (show = !show)"
                                name="action"
                            />
                        </template>
                    </UCard>
                </div>
            </template>
        </UCard>
    </UModal>
</template>
